import { useCallback } from 'react';

// Django selects the tab with `opentab=<code>`, the React profile with `tab=<id>`.
const DJANGO_TO_REACT_TAB = { perm: 'permissions' };

/**
 * Returns a builder for a user's profile URL (the React `/user?id=<id>` page).
 * Single source of truth: use it instead of composing the URL by hand.
 *
 * @example
 * const getUserProfileLink = useUserProfileLink();
 * window.open(getUserProfileLink(agent.id), '_blank');
 * getUserProfileLink(agent.id, { opentab: 'perm' }); // with extra query params
 *
 * @returns {(id: number|string, params?: Object<string, string>) => string}
 *   Absolute URL; `params` are appended as extra query params. `opentab` is
 *   the Django tab param, sent as `tab=<react tab id>`.
 */
function useUserProfileLink() {
  return useCallback((id, params = {}) => {
    const url = new URL('/user', window.location.origin);

    url.searchParams.set('id', id);
    Object.entries(params).forEach(([key, value]) => {
      if (key === 'opentab') {
        url.searchParams.set('tab', DJANGO_TO_REACT_TAB[value] ?? value);
      } else {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  }, []);
}

export default useUserProfileLink;
